import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card } from 'flowbite-react';
import { HiOutlineCpuChip } from 'react-icons/hi2';
import { GrMemory } from 'react-icons/gr';
import { IoIosTrendingUp } from 'react-icons/io';
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import { VscGitFetch } from 'react-icons/vsc';
import { MdOutlineNotificationsActive } from 'react-icons/md';
import * as echarts from 'echarts';
import { FormatDate } from '../../../hooks/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import CpuUsageSkeleton from '../../../components/Skeletons/CardSkeleton';
import ChartSkeleton from '../../../components/Skeletons/ChartSkeleton';
import SingleLineSkeleton from '../../../components/Skeletons/SingleLineSkeleton';
import { toast } from 'react-toastify';
import { NavLink } from 'react-router-dom';

const ServerMetrics = () => {
  
  document.title = 'Server Metrics - Cloudvantage';
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pathArray = pathname.split('/');
  const projectId = pathArray[2];
  const serverId = pathArray[pathArray.length - 1];
  const cpuChartRef = useRef(null);
  const memoryChartRef = useRef(null);
  const serverDiskRef = useRef(null);
  const serverEgressRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDiskLoading, setDiskIsLoading] = useState(false);
  const [isEgressLoading, setEgressLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [serverMetrics, setServerMetrics] = useState({
    avg_cpu_usage: '',
    avg_mem_usage: '',
    recent_cpu_usage: '',
    recent_mem_usage: '',
    last_updated: '',
    cpu_usage_data: [],
    memory_usage_data: [],
  });
  const [diskMetrics, setDiskMetrics] = useState({
    serverName: '',
    metrics: [],
    disk_total: '',
  });
  const [serverEgress, setServerEgress] = useState({
    current_month_max_egress: '',
    egress_points: [],
  });
  const diskDateList = diskMetrics.metrics.map((item) =>
    FormatDate(item.timestamp),
  );

  const diskUsedList = diskMetrics.metrics.map((item) => item.disk_usage_gb);
  const diskAvailableList = diskMetrics.metrics.map(
    (item) => item.disk_available_gb,
  );

  const cpu_usage_data = serverMetrics.cpu_usage_data.map(
    ({ timestamp, cpu_usage_pts }) => [FormatDate(timestamp), cpu_usage_pts],
  );
  const memory_usage_data = serverMetrics.memory_usage_data.map(
    ({ timestamp, memory_usage_pts, used_memory }) => [
      FormatDate(timestamp),
      memory_usage_pts,
      used_memory,
    ],
  );
  const egress_points_data = serverEgress.egress_points.map(
    ({ timestamp, value }) => [FormatDate(timestamp), value],
  );

  const egressDateList = egress_points_data.map((item) => item[0]);
  const egressValueList = egress_points_data.map((item) => item[1]);
  const cpuDateList = cpu_usage_data.map(function (item) {
    return item[0];
  });
  const cpuValueList = cpu_usage_data.map(function (item) {
    return item[1];
  });
  const memoryDateList = memory_usage_data.map(function (item) {
    return item[0];
  });
  const memoryValueList = memory_usage_data.map(function (item) {
    return item[1];
  });
  const cpuOption = useMemo(
    () => ({
      visualMap: [
        {
          show: false,
          type: 'continuous',
          seriesIndex: 0,
          min: 0,
          max: 400,
        },
      ],
      title: {
        text: 'CPU Usage',
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
      },
      xAxis: {
        type: 'category',
        data: cpuDateList,
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          data: cpuValueList,
          type: 'line',
          smooth: true,
          showSymbol: false,
          lineStyle: {
            color: '#14AE5C',
            width: 3,
          },
        },
      ],
    }),
    [cpuValueList, cpuDateList],
  );
  const memoryOption = useMemo(
    () => ({
      visualMap: [
        {
          show: false,
          type: 'continuous',
          seriesIndex: 0,
          min: 0,
          max: 400,
        },
      ],
      title: {
        text: 'Memory Usage',
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
      },
      xAxis: {
        type: 'category',
        data: memoryDateList,
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          data: memoryValueList,
          type: 'line',
          smooth: true,
          showSymbol: false,
          lineStyle: {
            color: '#2563eb',
            width: 3,
          },
        },
      ],
    }),
    [memoryDateList, memoryValueList],
  );
  const serverDiskOption = useMemo(
    () => ({
      title: {
        text: `Server Disk Usage`,
        subtext: `Name: ${diskMetrics.serverName} | Total: ${diskMetrics.disk_total} GB`,
      },
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: ['Used', 'Available'],
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: diskDateList,
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          name: 'Used',
          type: 'line',
          stack: 'Total',
          data: diskUsedList,
        },
        {
          name: 'Available',
          type: 'line',
          stack: 'Total',
          data: diskAvailableList,
        },
      ],
    }),
    [
      diskAvailableList,
      diskDateList,
      diskMetrics.disk_total,
      diskMetrics.serverName,
      diskUsedList,
    ],
  );

  const egressOption = useMemo(
    () => ({
      visualMap: [
        {
          show: false,
          type: 'continuous',
          seriesIndex: 0,
          min: 0,
          max: 10000,
        },
      ],
      title: {
        text: `Egress Usage- (Current Month Max: ${serverEgress.current_month_max_egress} )`,
      },
      tooltip: {
        trigger: 'axis',
      },
      xAxis: {
        type: 'category',
        data: egressDateList,
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          data: egressValueList,
          type: 'line',
          smooth: true,
          showSymbol: false,
          areaStyle: {
            color: '#14AE5C',
            width: 3,
          },
        },
      ],
    }),
    [serverEgress.current_month_max_egress, egressDateList, egressValueList],
  );

  const handleFetchServerMetrics = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await apiEndPoints.fetchServerMetrics(
        projectId,
        serverId,
        startDate,
        endDate,
      );
      setServerMetrics({
        avg_cpu_usage:
          data.server.summary_resource_usage?.average_cpu_usage_pts ?? '',
        avg_mem_usage:
          data.server.summary_resource_usage?.average_memory_usage_pts ?? '',
        recent_cpu_usage:
          data.server.summary_resource_usage?.recent_cpu_usage_pts ?? '',
        recent_mem_usage:
          data.server.summary_resource_usage?.recent_memory_usage_pts ?? '',
        last_updated: data.server.summary_resource_usage?.last_updated ?? '',
        cpu_usage_data: data.server.cpu_usage_pts ?? [],
        memory_usage_data: data.server.memory_usage_pts ?? [],
      });
    } catch (error) {
      toast.error('Error fetching server metrics');
    } finally {
      setIsLoading(false);
    }
  }, [endDate, projectId, serverId, startDate]);

  const handleFetchServerDiskMetrics = useCallback(async () => {
    setDiskIsLoading(true);
    try {
      const { data } = await apiEndPoints.fetchServerDiskMetrics(
        projectId,
        serverId,
        startDate,
        endDate,
      );
      setDiskMetrics({
        serverName: data.metrics[0].metadata.server_name || '',
        metrics: data.metrics || [],
        disk_total: data.metrics[0].disk_total_gb || '',
      });
    } catch (error) {
      if (error.response) {
        toast.error('Error fetching server disk metrics');
      }
    } finally {
      setDiskIsLoading(false);
    }
  }, [endDate, projectId, serverId, startDate]);

  const handleFetchServerEgress = useCallback(async () => {
    setEgressLoading(true);
    try {
      const { data } = await apiEndPoints.fetchServerEgress(
        projectId,
        serverId,
      );
      setServerEgress({
        current_month_max_egress: data.current_month_max_egress || '',
        egress_points: data.egress_points || [],
      });
    } catch (error) {
      if (error.response) {
        toast.error('Error fetching server egress');
      }
    } finally {
      setEgressLoading(false);
    }
  }, [projectId, serverId]);
  useEffect(() => {
    let cpuChart, memoryChart;
    if (cpuChartRef.current && memoryChartRef.current) {
      cpuChart = echarts.init(cpuChartRef.current);
      memoryChart = echarts.init(memoryChartRef.current);
      cpuChart.setOption(cpuOption);
      memoryChart.setOption(memoryOption);
      window.addEventListener('resize', cpuChart.resize);
      window.addEventListener('resize', memoryChart.resize);
    }

    return () => {
      if (cpuChart && memoryChart) {
        window.removeEventListener('resize', cpuChart.resize);
        window.removeEventListener('resize', memoryChart.resize);
        cpuChart.dispose();
        memoryChart.dispose();
      }
    };
  }, [cpuOption, memoryOption, handleFetchServerMetrics]);
  useEffect(() => {
    let diskChart;
    if (serverDiskRef.current) {
      diskChart = echarts.init(serverDiskRef.current);
      diskChart.setOption(serverDiskOption);
      window.addEventListener('resize', diskChart.resize);
    }
    return () => {
      if (diskChart) {
        window.removeEventListener('resize', diskChart.resize);
        diskChart.dispose();
      }
    };
  }, [serverDiskOption, handleFetchServerDiskMetrics]);
  useEffect(() => {
    let egressChart;
    if (serverEgressRef.current) {
      egressChart = echarts.init(serverEgressRef.current);
      egressChart.setOption(egressOption);
      window.addEventListener('resize', egressChart.resize);
    }
    return () => {
      if (egressChart) {
        window.removeEventListener('resize', egressChart.resize);
        egressChart.dispose();
      }
    };
  }, [egressOption, handleFetchServerEgress]);

  useEffect(() => {
    handleFetchServerMetrics();
    handleFetchServerDiskMetrics();
    handleFetchServerEgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, serverId]);

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            color=""
            size="sm"
            className=" text-primaryBlue cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft size={20} />
          </Button>
          <p className="crimson-font text-xl font-medium text-primaryBlue">
            Server Details
          </p>
        </div>
        <Button
          className="py-2 px-3 cursor-pointer bg-[#1da1f1]"
          size="sm"
          onClick={handleFetchServerMetrics}
          disabled={false}
        >
          <FiRefreshCw className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-row gap-6 mt-8">
        {isLoading ? (
          <CpuUsageSkeleton />
        ) : (
          <Card className="w-1/4">
            <div className="h-[95px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <h5 className="text-md">Current CPU Usage</h5>
                <HiOutlineCpuChip size="25px" color="#14AE5C" />
              </div>
              <div>
                <h5 className="text-xl font-bold">
                  {serverMetrics.recent_cpu_usage || '0.0'}
                </h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Avg: {`${serverMetrics.avg_cpu_usage}%` || '0.0%'}
                </p>
              </div>
            </div>
          </Card>
        )}
        {isLoading ? (
          <CpuUsageSkeleton />
        ) : (
          <Card className="w-1/4">
            <div className="h-[95px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <h5 className="text-md">Current Memory Usage</h5>
                <GrMemory size="25px" color="purple" />
              </div>
              <div>
                <h5 className="text-xl font-bold">
                  {serverMetrics.recent_mem_usage || '0.0'}
                </h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Avg: {`${serverMetrics.recent_mem_usage}%` || '0.0%'}
                </p>
              </div>
            </div>
          </Card>
        )}
        {isLoading ? (
          <CpuUsageSkeleton />
        ) : (
          <Card className="w-1/4">
            <div className="h-[95px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <h5 className="text-md">CPU Trend</h5>
                <IoIosTrendingUp
                  size="25px"
                  color={
                    serverMetrics.avg_cpu_usage > 0.5
                      ? 'red' // Increasing
                      : serverMetrics.avg_cpu_usage < 0.2
                        ? '#BE7E30' // Reducing
                        : 'blue' // Stable
                  }
                />
              </div>
              <div>
                <h5
                  className={`text-xl font-bold ${
                    serverMetrics.avg_cpu_usage > 0.5
                      ? 'text-[#D21D1D]'
                      : serverMetrics.avg_cpu_usage < 0.2
                        ? 'text-blue-500'
                        : 'text-green-500'
                  }`}
                >
                  {serverMetrics.avg_cpu_usage > 0.5
                    ? 'Increasing'
                    : serverMetrics.avg_cpu_usage < 0.2
                      ? 'Reducing'
                      : 'Stable'}
                </h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Avg: {`${serverMetrics.avg_cpu_usage}%` || '0.0%'}
                </p>
              </div>
            </div>
          </Card>
        )}
        {isLoading ? (
          <CpuUsageSkeleton />
        ) : (
          <Card className="w-1/4">
            <div className="h-[95px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <h5 className="text-md">Memory Trend</h5>
                <IoIosTrendingUp
                  size="25px"
                  color={
                    serverMetrics.avg_mem_usage > 20
                      ? 'red' // Increasing
                      : serverMetrics.avg_cpu_usage < 0.2
                        ? '#BE7E30' // Reducing
                        : 'blue' // Stable
                  }
                />
              </div>
              <div>
                <h5
                  className={`text-xl font-bold ${
                    serverMetrics.avg_mem_usage > 20
                      ? 'text-[#D21D1D]'
                      : serverMetrics.avg_mem_usage < 10
                        ? 'text-blue-500'
                        : 'text-green-500'
                  }`}
                >
                  {serverMetrics.avg_mem_usage > 20
                    ? 'Increasing'
                    : serverMetrics.avg_mem_usage < 10
                      ? 'Reducing'
                      : 'Stable'}
                </h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Avg: {`${serverMetrics.avg_mem_usage}%` || '0.0%'}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
      <div className="flex justify-between items-center gap-4 mt-8 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-4">
        <div className=" w-1/2 flex justify-between">
          <label className="text-sm font-medium">
            Start Date:
            <input
              type="date"
              className="ml-2 px-2 py-1 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-black dark:text-white focus:border-gray-300 focus:ring-0"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
              }}
            />
          </label>
          <label className="text-sm font-medium">
            End Date:
            <input
              type="date"
              className="ml-2 px-2 py-1 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-black dark:text-white focus:border-gray-300 focus:ring-0"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
              }}
            />
          </label>
        </div>
        {isLoading ? (
          <SingleLineSkeleton width="250px" />
        ) : (
          <p className="text-sm">
            Last updated:
            {serverMetrics?.last_updated
              ? FormatDate(serverMetrics.last_updated)
              : '00:00:00'}
          </p>
        )}

        <Button
          size="sm"
          onClick={handleFetchServerMetrics}
          className="flex items-center py-2 px-3 cursor-pointer bg-[#1da1f1]"
        >
          <VscGitFetch className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Fetch
        </Button>
        <NavLink
          className="py-2 px-3 rounded-md border border-red-500 bg-white text-red-500"
          to={{
            pathname: `/${projectId}/servers/${serverId}/server-alarming`,
          }}
        >
          <MdOutlineNotificationsActive className={`mr-1 inline`} />
          Alarming
        </NavLink>
      </div>
      <div className="mt-0 flex gap-5">
        {isLoading ? (
          <ChartSkeleton />
        ) : (
          <div
            ref={cpuChartRef}
            className="w-1/2 h-[400px] bg-white dark:bg-gray-800 rounded-lg shadow"
          />
        )}

        {isLoading ? (
          <ChartSkeleton />
        ) : (
          <div
            ref={memoryChartRef}
            className="w-1/2 h-[400px] bg-white dark:bg-gray-800 rounded-lg shadow"
          />
        )}
      </div>
      <div className="mt-2 flex gap-5">
        {isDiskLoading ? (
          <ChartSkeleton />
        ) : (
          <div
            ref={serverDiskRef}
            className="w-1/2 h-[400px] mt-4 bg-white dark:bg-gray-800 rounded-lg shadow"
          />
        )}
        {isEgressLoading ? (
          <ChartSkeleton />
        ) : (
          <div
            ref={serverEgressRef}
            className="w-1/2 h-[400px] mt-4 bg-white dark:bg-gray-800 rounded-lg shadow"
          />
        )}
      </div>
    </div>
  );
};
export default ServerMetrics;
